import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PageHero from '../components/PageHero';
import { formatDate } from '../utils/formatDate';
import { API_URL } from '../config';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([getBlogs(), getCategories(), getHeroData()]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kategoris?populate=*`);
      console.log('Categories response:', response.data);
      if (response.data?.data) {
        const transformedCategories = response.data.data.map(category => ({
          id: category.id,
          name: category.name,
          slug: category.slug,
          description: category.description
        }));
        console.log('Transformed categories:', transformedCategories);
        setCategories(transformedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error.response || error);
      setError('Kategoriler yüklenirken bir hata oluştu.');
    }
  };

  const getHeroData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/heroes?populate=*`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.data && result.data[0]) {
        const heroItem = result.data[0];
        setHeroData({
          title: 'Blog',
          subtitle: 'Hukuki Makaleler ve Güncel Bilgiler',
          image: heroItem.image?.url ? 
            `${API_URL}${heroItem.image.url}` : null
        });
      }
    } catch (error) {
      console.error('Error fetching hero:', error);
    }
  };

  const getBlogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blogs?populate=*`);
      console.log('Blogs response:', response.data);
      if (response.data?.data) {
        const transformedBlogs = response.data.data.map(blog => {
          const blogData = {
            id: blog.id,
            title: blog.title,
            description: blog.description,
            publishedAt: blog.publishedAt,
            image: null,
            category: null
          };

          // Image handling
          if (blog.image?.url) {
            blogData.image = `${API_URL}${blog.image.url}`;
          }

          // Category handling
          if (blog.category) {
            blogData.category = {
              id: blog.category.id,
              name: blog.category.name,
              slug: blog.category.slug,
              description: blog.category.description
            };
          }

          return blogData;
        });
        console.log('Transformed blogs:', transformedBlogs);
        setBlogs(transformedBlogs);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error.response || error);
      setError('Blog yazıları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = selectedCategory === 'all'
    ? blogs
    : blogs.filter(blog => blog.category && blog.category.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero
        title={heroData?.title || 'Blog'}
        subtitle={heroData?.subtitle}
        image={heroData?.image}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Kategori Filtreleme */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full ${selectedCategory === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Tümü
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.slug)}
                className={`px-4 py-2 rounded-full ${selectedCategory === category.slug
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Listesi */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : error ? (
          <div className="text-center text-red-600 py-8">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blog/${blog.id}`}
                onClick={() => window.scrollTo(0, 0)}
                className="group h-full"
              >
                <div className="border-2 border-gray-200 rounded-lg hover:border-primary transition-colors duration-300 h-full flex flex-col overflow-hidden">
                  {blog.image && (
                    <div className="h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    {blog.category?.name && (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium text-primary bg-primary/10 rounded-full mb-2 w-fit">
                        {blog.category.name}
                      </span>
                    )}
                    <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {blog.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {blog.description}
                    </p>
                    <div className="text-sm text-gray-500 mt-auto">
                      {formatDate(blog.publishedAt)}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
